import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { MapContainer, TileLayer, GeoJSON } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

const MEXICO_GEO_URL =
    'https://raw.githubusercontent.com/angelnmara/geojson/master/mexicoHigh.json';

/** Strip accents and lowercase for fuzzy name matching */
const stripAcc = s =>
    (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

/** Common name divergences between the GeoJSON source and CNE API */
const GEO_ALIASES = {
    'distrito federal':  'ciudad de mexico',
    'veracruz llave':    'veracruz de ignacio de la llave',
    'veracruz':          'veracruz de ignacio de la llave',
    'michoacan ocampo':  'michoacan',
    'coahuila':          'coahuila de zaragoza',
    'mexico':            'estado de mexico',
};

/** Try every common GeoJSON property key that might hold the state name */
const getFeatureName = props =>
    props?.name || props?.NOM_ENT || props?.NOMGEO || props?.nombre || props?.Estado || '';

/** Return the [key, stats] pair in statePrices that best matches featureName */
const matchState = (featureName, statePrices) => {
    let fn = stripAcc(featureName);
    fn = GEO_ALIASES[fn] || fn;
    for (const [k, v] of Object.entries(statePrices)) {
        const kn = stripAcc(k);
        if (kn === fn || fn.includes(kn) || kn.includes(fn)) return [k, v];
    }
    return null;
};

/** Interpolate green → red based on normalised position [0, 1] */
const priceColor = t =>
    `rgb(${Math.round(t * 200 + 30)}, ${Math.round((1 - t) * 170 + 30)}, 40)`;

// ── Component ─────────────────────────────────────────────────────────────────

const DieselMap = ({ results, selectedCategories }) => {
    const [geoData,       setGeoData]       = useState(null);
    const [geoError,      setGeoError]      = useState(null);
    const [selectedState,        setSelectedState]        = useState(null);
    const [expandedMunicipality, setExpandedMunicipality] = useState(null);

    // Fetch Mexico states GeoJSON once on mount
    useEffect(() => {
        let active = true;
        fetch(MEXICO_GEO_URL)
            .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
            .then(d  => { if (active) setGeoData(d); })
            .catch(() => { if (active) setGeoError('No se pudo cargar el mapa base (requiere conexión a internet).'); });
        return () => { active = false; };
    }, []);

    // Close municipality panel and expanded row when category or state switches
    useEffect(() => { setSelectedState(null); setExpandedMunicipality(null); }, [selectedCategories]);
    useEffect(() => { setExpandedMunicipality(null); }, [selectedState]);

    // ── Metrics per state ─────────────────────────────────────────────────────

    const statePrices = useMemo(() => {
        const base = selectedCategories.includes('Todos')
            ? results
            : results.filter(r => selectedCategories.includes(r.categoria));
        const by = {};
        base.forEach(r => {
            const k = (r.entidadNombre || '').trim();
            const p = parseFloat(r.PrecioVigente);
            if (!k || isNaN(p) || p <= 0) return;
            if (!by[k]) by[k] = [];
            by[k].push(p);
        });
        const out = {};
        for (const [k, ps] of Object.entries(by)) {
            out[k] = {
                avg:   ps.reduce((a, b) => a + b, 0) / ps.length,
                min:   Math.min(...ps),
                max:   Math.max(...ps),
                count: ps.length,
            };
        }
        return out;
    }, [results, selectedCategories]);

    const { pMin, pMax } = useMemo(() => {
        const avgs = Object.values(statePrices).map(s => s.avg);
        return avgs.length
            ? { pMin: Math.min(...avgs), pMax: Math.max(...avgs) }
            : { pMin: 0, pMax: 1 };
    }, [statePrices]);

    const tNorm = avg => pMax === pMin ? 0.5 : (avg - pMin) / (pMax - pMin);

    // ── GeoJSON style & interaction ───────────────────────────────────────────

    const geoStyle = feature => {
        const m = matchState(getFeatureName(feature.properties), statePrices);
        if (!m) return { fillColor: '#ccc', weight: 1, color: '#aaa', fillOpacity: 0.5 };
        return {
            fillColor:   priceColor(tNorm(m[1].avg)),
            weight:      1.5,
            color:       '#555',
            fillOpacity: 0.78,
        };
    };

    const onEachFeature = (feature, layer) => {
        const name = getFeatureName(feature.properties);
        const m    = matchState(name, statePrices);
        const d    = m ? m[1] : null;
        layer.bindTooltip(
            d
                ? `<b>${name}</b><br>Promedio: <b>$${d.avg.toFixed(2)}</b><br>` +
                  `Mín $${d.min.toFixed(2)} — Máx $${d.max.toFixed(2)}<br>${d.count} estaciones`
                : `<b>${name}</b><br><i>Sin datos para esta categoría</i>`,
            { sticky: true }
        );
        layer.on({
            click:     ()  => setSelectedState(d ? { name, ...d } : null),
            mouseover: e  => e.target.setStyle({ weight: 3, fillOpacity: 0.95 }),
            mouseout:  e  => e.target.setStyle(geoStyle(feature)),
        });
    };

    // ── Municipality breakdown for clicked state ──────────────────────────────

    const municipalities = useMemo(() => {
        if (!selectedState) return [];
        const sn   = stripAcc(selectedState.name);
        const base = selectedCategories.includes('Todos')
            ? results
            : results.filter(r => selectedCategories.includes(r.categoria));
        const by = {};
        base
            .filter(r => {
                const en = stripAcc(r.entidadNombre || '');
                return en === sn || en.includes(sn) || sn.includes(en);
            })
            .forEach(r => {
                const k = r.municipioNombre || `Municipio ${r.municipioId}`;
                const p = parseFloat(r.PrecioVigente);
                if (isNaN(p) || p <= 0) return;
                if (!by[k]) by[k] = [];
                by[k].push(p);
            });
        return Object.entries(by)
            .map(([k, ps]) => ({
                name:  k,
                avg:   ps.reduce((a, b) => a + b, 0) / ps.length,
                min:   Math.min(...ps),
                max:   Math.max(...ps),
                count: ps.length,
            }))
            .sort((a, b) => a.avg - b.avg);
    }, [selectedState, results, selectedCategories]);

    // ── Stations breakdown for expanded municipality ───────────────────────────

    const stationsInMunicipality = useMemo(() => {
        if (!selectedState || !expandedMunicipality) return [];
        const sn = stripAcc(selectedState.name);
        const base = selectedCategories.includes('Todos')
            ? results
            : results.filter(r => selectedCategories.includes(r.categoria));
        return base
            .filter(r => {
                const en = stripAcc(r.entidadNombre || '');
                const mn = (r.municipioNombre || `Municipio ${r.municipioId}`);
                return (en === sn || en.includes(sn) || sn.includes(en)) && mn === expandedMunicipality;
            })
            .map(r => ({
                id:          r.Numero || r.id,
                nombre:      r.Nombre,
                precio:      parseFloat(r.PrecioVigente),
                producto:    r.Producto,
                descripcion: r.SubProducto,
                direccion:   r.Direccion,
            }))
            .sort((a, b) => a.precio - b.precio);
    }, [selectedState, expandedMunicipality, results, selectedCategories]);

    // ── Render ────────────────────────────────────────────────────────────────

    if (geoError) return (
        <Alert variant="warning">
            <i className="fas fa-exclamation-triangle mr-2"></i>{geoError}
        </Alert>
    );
    if (!geoData) return (
        <div className="text-center py-5">
            <Spinner animation="border" />
            <p className="mt-2 text-muted">Cargando mapa de México…</p>
        </div>
    );

    const munMin = municipalities[0]?.avg ?? 0;
    const munMax = municipalities[municipalities.length - 1]?.avg ?? munMin;

    return (
        <>
            {/* ── Leyenda ──────────────────────────────────────────────────── */}
            <div className="d-flex flex-wrap align-items-center mb-3 small" style={{ gap: 14 }}>
                <span className="font-weight-bold text-muted">{selectedCategories.includes('Todos') ? 'Todos' : selectedCategories.join(', ')}:</span>
                <div className="d-flex align-items-center" style={{ gap: 6 }}>
                    <span className="text-success font-weight-bold">${pMin.toFixed(2)}</span>
                    <div style={{
                        background: 'linear-gradient(to right, rgb(30,180,40), rgb(250,30,40))',
                        width: 90, height: 10, borderRadius: 3,
                    }} />
                    <span className="text-danger font-weight-bold">${pMax.toFixed(2)}</span>
                </div>
                <span className="text-muted">&#x25A1; los estados de gris no presentan éste combustible</span>
                <span className="text-primary">
                    <i className="fas fa-hand-pointer mr-1"></i>Haga clic en un estado para ver el detalle de costo por municipios
                </span>
            </div>

            {/* ── Mapa ─────────────────────────────────────────────────────── */}
            <div style={{ height: 460, borderRadius: 8, overflow: 'hidden', border: '1px solid #dee2e6' }}>
                <MapContainer
                    center={[23.6345, -102.5528]}
                    zoom={5}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <GeoJSON
                        key={selectedCategories.join(',')}
                        data={geoData}
                        style={geoStyle}
                        onEachFeature={onEachFeature}
                    />
                </MapContainer>
            </div>

            {/* ── Panel de municipios ───────────────────────────────────────── */}
            {selectedState && (
                <Card className="mt-3 shadow-sm">
                    <Card.Header className="py-2 d-flex justify-content-between align-items-center">
                        <h6 className="m-0 font-weight-bold">
                            <i className="fas fa-map-marker-alt mr-1 text-primary"></i>
                            {selectedState.name}
                            <span className="text-muted font-weight-normal ml-2">
                                — Prom. estatal:{' '}
                                <strong className="text-info">${selectedState.avg.toFixed(2)}</strong>
                            </span>
                            <Badge variant="info" className="ml-2 bg-dark text-light">
                                {municipalities.length} municipios
                            </Badge>
                        </h6>
                        <Button title="Cerrar panel de municipios" size="sm" variant="outline-secondary" onClick={() => setSelectedState(null)}>
                            <i className="fas fa-times"></i>
                        </Button>
                    </Card.Header>
                    <Card.Body className="p-0">
                        {municipalities.length === 0 ? (
                            <p className="p-3 mb-0 text-muted small">
                                Sin datos para esta categoría en este estado.
                            </p>
                        ) : (
                            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                                <Table striped hover size="sm" className="mb-0">
                                    <thead className="thead-light">
                                        <tr>
                                            <th style={{ width: 24 }}></th>
                                            <th>Municipio</th>
                                            <th className="text-right">Mín</th>
                                            <th className="text-right">Prom.</th>
                                            <th className="text-right">Máx</th>
                                            <th className="text-center">Est.</th>
                                            <th style={{ minWidth: 80 }}>Nivel</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {municipalities.map(m => {
                                            const t = munMax === munMin
                                                ? 0.5
                                                : (m.avg - munMin) / (munMax - munMin);
                                            const isExpanded = expandedMunicipality === m.name;
                                            return (
                                                <React.Fragment key={m.name}>
                                                    <tr
                                                        onClick={() => setExpandedMunicipality(isExpanded ? null : m.name)}
                                                        style={{ cursor: 'pointer', background: isExpanded ? '#eef2ff' : undefined }}
                                                    >
                                                        <td className="text-center" style={{ color: '#6c757d', fontSize: '0.7rem' }}>
                                                            <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                                                        </td>
                                                        <td className="font-weight-bold">{m.name}</td>
                                                        <td className="text-right text-success">${m.min.toFixed(2)}</td>
                                                        <td className="text-right font-weight-bold">${m.avg.toFixed(2)}</td>
                                                        <td className="text-right text-danger">${m.max.toFixed(2)}</td>
                                                        <td className="text-center">{m.count}</td>
                                                        <td>
                                                            <div style={{ background: '#e9ecef', borderRadius: 3, height: 12 }}>
                                                                <div style={{
                                                                    width:      `${Math.max(Math.round(t * 100), 4)}%`,
                                                                    background: priceColor(t),
                                                                    height:     '100%',
                                                                    borderRadius: 3,
                                                                }} />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    {isExpanded && (
                                                        <tr>
                                                            <td colSpan={7} style={{ padding: 0, background: '#f4f6ff' }}>
                                                                <div style={{ padding: '8px 12px 10px 28px' }}>
                                                                    {stationsInMunicipality.length === 0 ? (
                                                                        <p className="mb-0 text-muted small">Sin estaciones para esta categoría.</p>
                                                                    ) : (
                                                                        <Table size="sm" bordered className="mb-0" style={{ background: '#fff', fontSize: '0.78rem' }}>
                                                                            <thead>
                                                                                <tr style={{ background: '#e8eaf6' }}>
                                                                                    <th>#</th>
                                                                                    <th>Razón Social</th>
                                                                                    <th className="text-right">Precio</th>
                                                                                    <th>Producto</th>
                                                                                    <th style={{ minWidth: 100 }}>Dispersión</th>
                                                                                    <th>Dirección</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {(() => {
                                                                                    const sMin = stationsInMunicipality[0]?.precio ?? 0;
                                                                                    const sMax = stationsInMunicipality[stationsInMunicipality.length - 1]?.precio ?? sMin;
                                                                                    const sRange = sMax - sMin || 1;
                                                                                    return stationsInMunicipality.map((s, i) => {
                                                                                        const t = (s.precio - sMin) / sRange;
                                                                                        const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${s.nombre} ${s.direccion}, ${municipalities.name}, México`)}`;
                                                                                        return (
                                                                                            <tr
                                                                                                key={s.id ?? i}
                                                                                                onClick={() => window.open(mapsUrl, '_blank', 'noopener,noreferrer')}
                                                                                                title="Ver en Google Maps"
                                                                                                style={{ cursor: 'pointer' }}
                                                                                                onMouseEnter={e => e.currentTarget.style.background = '#e8f4fd'}
                                                                                                onMouseLeave={e => e.currentTarget.style.background = ''}
                                                                                            >
                                                                                                <td className="text-muted text-center">
                                                                                                    <i className="fas fa-map-marker-alt" style={{ color: '#ea4335', fontSize: '0.75rem' }}></i>
                                                                                                </td>
                                                                                                <td className="font-weight-bold">{s.nombre}</td>
                                                                                                <td className="text-right font-weight-bold text-success">${s.precio.toFixed(2)}</td>
                                                                                                <td>{s.producto}</td>
                                                                                                <td>
                                                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                                                                                        <div style={{ flex: 1, background: '#e9ecef', borderRadius: 3, height: 10 }}>
                                                                                                            <div style={{
                                                                                                                width: `${Math.max(Math.round(t * 100), 4)}%`,
                                                                                                                background: priceColor(t),
                                                                                                                height: '100%',
                                                                                                                borderRadius: 3,
                                                                                                            }} />
                                                                                                        </div>
                                                                                                        <span style={{ fontSize: '0.65rem', color: '#6c757d', whiteSpace: 'nowrap' }}>
                                                                                                            ${sMin.toFixed(2)}–${sMax.toFixed(2)}
                                                                                                        </span>
                                                                                                    </div>
                                                                                                </td>
                                                                                                <td className="text-muted small" style={{ maxWidth: 220 }}>{s.direccion}</td>
                                                                                            </tr>
                                                                                        );
                                                                                    });
                                                                                })()}
                                                                            </tbody>
                                                                        </Table>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            )}
        </>
    );
};

export default DieselMap;
